<template>
  <div class="form__field" :class="{ error: !status.valid }">
    <div class="searchable-select" :class="{ 'is-open': isOpen }">
      <div
        v-if="!isOpen"
        class="searchable-select__dropdown"
        :id="name"
        @click="openDropdown"
        tabindex="0"
        @keydown.enter="openDropdown"
        @keydown.space.prevent="openDropdown"
        :aria-invalid="!status.valid"
        :aria-describedby="`error__${name}`"
      >
        <span class="searchable-select__dropdown-value">
          {{ selectedLabel || t('search.typeToSearch') }}</span>
        <span class="searchable-select__dropdown-arrow">▼</span>
      </div>

      <input
        v-if="isOpen"
        ref="searchInput"
        type="text"
        :name="name"
        v-model="searchTerm"
        @blur="handleBlur"
        @keydown.down.prevent="navigateOptions(1)"
        @keydown.up.prevent="navigateOptions(-1)"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.escape="closeDropdown"
        :placeholder="t('search.typeToSearch')"
        :aria-invalid="!status.valid"
        :aria-describedby="`error__${name}`"
        autocomplete="off"
        class="searchable-select__input"
      />

      <ul v-show="isOpen" class="searchable-select__dropdown-options">
        <li
          v-for="(option, index) in displayedOptions"
          :key="option.name"
          :class="{ highlighted: index === highlightedIndex }"
          @mousedown.prevent="selectOption(option)"
          @mouseenter="highlightedIndex = index"
        >
          {{ option.label }}
        </li>

        <li v-if="filteredOptions.length === 0 && searchTerm" class="searchable-select__no-results">
          {{ t('search.noResults')}}
        </li>

        <li v-if="hasMoreResults" class="searchable-select__more-results">
          {{ t('search.numberOfResultsDisplayed').replace('{0}', displayedOptions.length.toString()).replace('{1}', filteredOptions.length.toString()) }}
        </li>

        <li v-if="!searchTerm && options.length > maxDisplayed" class="searchable-select__hint">
          {{ t('search.typeToSearchThroughOptions').replace('{0}', options.length.toString()) }}
        </li>
      </ul>
    </div>

    <label :for="name" v-if="!noLabel">
      {{ label ? label : name }}
      <span class="form__indicator" v-if="isRequired">*</span>
    </label>

    <span class="form__error-message" :id="`error__${name}`" v-if="!status.valid">
      {{ t('validation.select') }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { FormOption } from "@/types/formOption";
import { validate, Status } from '@/validation'
import { required, Rule } from '@/validation/rules'
import { ref, watch, computed, nextTick } from "vue";
import { useI18n } from "vue3-i18n";

// eslint-disable-next-line
const props = defineProps<{
  name: string;
  modelValue: string;
  label?: string;
  rules?: Rule[];
  options: FormOption[];
  noLabel?: boolean;
  maxDisplayed?: number;
}>();

// eslint-disable-next-line
defineExpose({
  validateInput
});

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "update:modelValue", value: string): void;
  (event: "validated", name: string, validationStatus: Status): void;
}>();

const { t } = useI18n();
const status = ref<Status>({ valid: true });
const isRequired = !(props.rules != null && props.rules.length == 0);
const inputValue = ref<string>(props.modelValue as string);
const searchTerm = ref("");
const isOpen = ref(false);
const highlightedIndex = ref(0);
const maxDisplayed = props.maxDisplayed || 100;
const searchInput = ref<HTMLInputElement | null>(null);

// Filter options based on search term
const filteredOptions = computed(() => {
  if (!searchTerm.value) {
    return props.options.slice(0, maxDisplayed);
  }

  const term = searchTerm.value.toLowerCase();
  const filtered: FormOption[] = [];

  // Fast filtering - stop after we find enough results
  for (let i = 0; i < props.options.length && filtered.length < maxDisplayed * 2; i++) {
    const option = props.options[i];
    if (option.label.toLowerCase().includes(term) ||
      option.name.toLowerCase().includes(term)) {
      filtered.push(option);
    }
  }

  return filtered;
});

// Limit displayed options
const displayedOptions = computed(() =>
  filteredOptions.value.slice(0, maxDisplayed)
);

const hasMoreResults = computed(() =>
  filteredOptions.value.length > maxDisplayed
);

// Get label for selected value
const selectedLabel = computed(() => {
  if (!inputValue.value) return "";
  const selected = props.options.find(opt => opt.name === inputValue.value);
  return selected?.label || "";
});

watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue;
});

watch(searchTerm, () => {
  highlightedIndex.value = 0;
});

async function openDropdown() {
  isOpen.value = true;
  await nextTick();
  searchInput.value?.focus();
}

function selectOption(option: FormOption) {
  inputValue.value = option.name;
  searchTerm.value = "";
  isOpen.value = false;
  handleChange();
}

function navigateOptions(direction: number) {
  if (!isOpen.value || displayedOptions.value.length === 0) return;

  highlightedIndex.value = Math.max(
    0,
    Math.min(displayedOptions.value.length - 1, highlightedIndex.value + direction)
  );
}

function selectHighlighted() {
  if (isOpen.value && displayedOptions.value[highlightedIndex.value]) {
    selectOption(displayedOptions.value[highlightedIndex.value]);
  }
}

function closeDropdown() {
  isOpen.value = false;
  searchTerm.value = "";
}

function handleChange() {
  validateInput();
  emit("update:modelValue", inputValue.value);
}

function handleBlur() {
  setTimeout(() => {
    isOpen.value = false;
    searchTerm.value = "";
    validateInput();
  }, 200);
}

function validateInput() {
  let validationRules = props.rules ? props.rules : [required]
  status.value = validate(inputValue.value, validationRules)
  emit("validated", props.name, status.value);
}
</script>